import { ALL_BLOCKS, AM_BLOCKS, PM_BLOCKS, BLOCK_LABELS } from "../lib/blocks";

/* ───────────────────── DAY DETAIL PANEL ───────────────────── */
export function DayDetailPanel({ dateKey, dateHeat, playerList, playerColorMap, staffHeat, staffList }) {
  if (!dateKey) return null;
  const dt = new Date(dateKey + "T12:00:00");
  const label = dt.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const entries = dateHeat[dateKey] || [];
  // entries: [{name, blocks}] or ["name"]
  const unavailNames = entries.map((e) => typeof e === "object" ? e.name : e);
  const availPlayers = playerList.filter((n) => !unavailNames.includes(n));

  // Build block detail for unavailable players
  const unavailDetail = entries.map((e) => {
    if (typeof e === "object") {
      const b = typeof e.blocks === "string" ? e.blocks.split(",").filter(Boolean) : (e.blocks || ALL_BLOCKS);
      return { name: e.name, blocks: b, isFull: b.length === ALL_BLOCKS.length };
    }
    return { name: e, blocks: [...ALL_BLOCKS], isFull: true };
  });

  function blockLabel(blocks) {
    if (blocks.length === ALL_BLOCKS.length) return "Full day";
    if (blocks.length === AM_BLOCKS.length && AM_BLOCKS.every((b) => blocks.includes(b))) return "AM";
    if (blocks.length === PM_BLOCKS.length && PM_BLOCKS.every((b) => blocks.includes(b))) return "PM";
    return blocks.map((b) => BLOCK_LABELS[b] || b).join(", ");
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6">
      <h3 className="font-bold text-gray-800 mb-3">{label}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <h4 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
            Available ({availPlayers.length})
          </h4>
          {availPlayers.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No players available</p>
          ) : (
            <div className="space-y-1">
              {availPlayers.map((name) => (
                <div key={name} className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: playerColorMap[name] }} />
                  {name}
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
            Unavailable ({unavailDetail.length})
          </h4>
          {unavailDetail.length === 0 ? (
            <p className="text-xs text-gray-400 italic">Everyone is available!</p>
          ) : (
            <div className="space-y-1.5">
              {unavailDetail.map(({ name, blocks, isFull }) => (
                <div key={name} className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: playerColorMap[name] }} />
                  <span className="flex-1">{name}</span>
                  <span className={"text-xs px-1.5 py-0.5 rounded-full " +
                    (isFull ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700")}>
                    {blockLabel(blocks)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Coaches and staff for this date. Their own row, never folded into the
          player counts above — "9 of 11 available" has to mean players. */}
      {(staffList || []).length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <h4 className="text-sm font-semibold text-sky-800 mb-2">Coaches &amp; staff</h4>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {staffList.map((name) => {
              const entry = ((staffHeat || {})[dateKey] || []).find((e) => e.name === name);
              const blocks = entry
                ? (typeof entry.blocks === "string" ? entry.blocks.split(",").filter(Boolean) : entry.blocks || [])
                : [];
              return (
                <span key={name} className="flex items-center gap-1.5 text-sm text-gray-700">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: playerColorMap[name] }} />
                  {name}
                  <span className={"text-xs px-1.5 py-0.5 rounded-full " +
                    (!entry ? "bg-green-100 text-green-700"
                      : blocks.length === ALL_BLOCKS.length ? "bg-red-100 text-red-700"
                      : "bg-amber-100 text-amber-700")}>
                    {!entry ? "Available" : blockLabel(blocks)}
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Time block grid summary */}
      {unavailDetail.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <h4 className="text-xs font-semibold text-gray-500 mb-2">Availability by Time Block</h4>
          <div className="grid grid-cols-7 gap-1">
            {ALL_BLOCKS.map((b) => {
              const unavailInBlock = unavailDetail.filter((p) => p.blocks.includes(b)).length;
              const availInBlock = playerList.length - unavailInBlock;
              const ratio = playerList.length > 0 ? availInBlock / playerList.length : 1;
              let bg = "#dcfce7";
              if (ratio < 1 && ratio >= 0.75) bg = "#ecfdf5";
              else if (ratio < 0.75 && ratio >= 0.5) bg = "#fef9c3";
              else if (ratio < 0.5 && ratio > 0) bg = "#fee2e2";
              else if (ratio === 0) bg = "#fca5a5";
              return (
                <div key={b} className="text-center rounded-lg py-1.5" style={{ backgroundColor: bg }}>
                  <div className="text-[10px] font-medium text-gray-600">{BLOCK_LABELS[b]}</div>
                  <div className="text-xs font-bold text-gray-800">{availInBlock}/{playerList.length}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
